import { useEffect, useState } from "react";
import qscoinprice from "../../data/qscoinprice";
import DiscountBanner from "../DiscountBanner";

const MiscCalculator = () => {
  const [coinprices, setcoinPrices] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [targetCoins, setTargetCoins] = useState(1000000); // default 1M
  const [includeDiscount, setIncludeDiscount] = useState(true); // New state for discount toggle

  // Load coin prices and initialize
  useEffect(() => {
    const fetchData = async () => {
      try {
        const coins = [];
        for (const key in qscoinprice) {
          const item = qscoinprice[key];
          if (item.itemid.startsWith("itm_coin")) {
            coins.push(item);
          }
        }
        setcoinPrices(coins);
        setIsInitialized(true);
      } catch (error) {
        console.log("Issue setting coin prices for calculator: ", error);
        setIsInitialized(true);
      }
    };
    fetchData();
  }, []);

  // Calculate how many bundles needed and total cost for target coins
  const calculateBundleCosts = () => {
    if (!isInitialized) return [];
    return coinprices
      .filter((bundle) => bundle.itemid.startsWith("itm_coin"))
      .map((bundle) => {
        const discountMultiplier = includeDiscount ? 0.95 : 1; // Apply 5% discount if toggle is on
        const discountedPrice = bundle.price * discountMultiplier;

        const bundlesNeeded = Math.ceil(targetCoins / bundle.amount);
        const totalCost = bundlesNeeded * discountedPrice;
        const costPerCoin = totalCost / targetCoins;
        const totalCoins = bundlesNeeded * bundle.amount;

        return {
          name: bundle.name,
          bundlesNeeded,
          totalCost,
          costPerCoin,
          totalCoins,
          originalPrice: bundle.price,
          discountedPrice: includeDiscount ? discountedPrice : null,
        };
      });
  };

  const bundleResults = calculateBundleCosts();

  return (
    <div className="flex flex-col md:flex-row gap-4 p-4">
      {/* Coin Bundle & Conversion */}
      <div className="calculator-style">
        <h2 className="calculator-title mb-4">Coin Bundle Getter</h2>

        {/* Target Coins to Cost Calculation */}
        <div className="calc-card">
          <div className="mb-2">
            <label className="block mb-2 font-medium">Target Coins</label>
            <input
              type="text"
              value={targetCoins.toLocaleString()}
              onChange={(e) => {
                const rawValue = parseInt(e.target.value.replace(/,/g, "")) || 0;
                setTargetCoins(rawValue);
              }}
              className="w-full p-2 border rounded calculator-form"
            />
          </div>

          {/* Discount Toggle */}
          <div className="mb-4 flex items-center">
            <label className="inline-flex items-center cursor-pointer gap-2">
              <div className="relative">
                <input type="checkbox" checked={includeDiscount} onChange={() => setIncludeDiscount(!includeDiscount)} className="sr-only peer" />
                <div className="w-6 h-3 bg-gray-300 rounded-full peer peer-checked:bg-[var(--level-bars)] transition-colors duration-200">
                  <div
                    className={`absolute top-1/2 w-2 h-2 bg-white rounded-full shadow-sm transform -translate-y-1/2 transition-all duration-200 ${
                      includeDiscount ? "translate-x-3" : "translate-x-1"
                    }`}
                  ></div>
                </div>
              </div>
              <span className="text-xs font-medium text-[var(--text-color4)] select-none">Include 5% Discount</span>
            </label>
          </div>

          {/* Results table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2">Bundle</th>
                  <th className="px-4 py-2">Bundles Needed</th>
                  <th className="px-4 py-2">
                    Total Cost ($PIXEL)
                    {includeDiscount && <span className="text-xs text-green-500"> (with 5% off)</span>}
                  </th>
                  <th className="px-4 py-2">Total Coins</th>
                  <th className="px-4 py-2">
                    Cost per Coin
                    {includeDiscount && <span className="text-xs text-green-500"> (discounted)</span>}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-center">
                {bundleResults.map((res, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2">{res.name}</td>
                    <td className="px-4 py-2">{res.bundlesNeeded}</td>
                    <td className="px-4 py-2">
                      {includeDiscount && (
                        <span className="line-through text-gray-400 text-xs mr-1">{(res.bundlesNeeded * res.originalPrice).toLocaleString()}</span>
                      )}
                      {res.totalCost.toLocaleString()}
                    </td>
                    <td className="px-4 py-2">{res.totalCoins.toLocaleString()} coins</td>
                    <td className="px-4 py-2">
                      {includeDiscount && (
                        <span className="line-through text-gray-400 text-xs mr-1">{((res.bundlesNeeded * res.originalPrice) / targetCoins).toFixed(6)}</span>
                      )}
                      {res.costPerCoin.toFixed(6)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='text-center'><DiscountBanner /></div>
        </div>
      </div>
    </div>
  );
};

export default MiscCalculator;
